/**
 * Adapts the v1 `POST /client/alerts/preview` response (DashboardChartResponse / LineChart)
 * into the shapes consumed by the v2 Line chart strategy.
 *
 * The alert preview response represents all series — the primary rating line and the optional
 * alert-bound lines — as long-format points inside `charts[0].values`, identified by the
 * `category` string.  The naming convention is:
 *
 *   - `category.includes('Alert Bounds')` → a bound series (not the main metric)
 *   - `category.includes('Lower')`        → lower bound series
 *   - `category.includes('Upper')`        → upper bound series
 *
 * These conventions are documented as a silent-failure trap in charts-v2/AGENTS.md —
 * all consumers of this response should key off these substrings, not hard-coded labels.
 *
 * Index-alignment invariant: both `chart.data` and `referenceBounds.lower/upper` are built
 * from the same `sortedBuckets` array (chronologically sorted normalized x_values).
 * v2's `transformForBasicChart` sorts chart.data by `rawTimestamp` ascending, which is a
 * no-op since `sortedBuckets` is already sorted — so `referenceBounds[i]` stays aligned
 * with rendered row `i`.
 */

import type { Chart as ChartV2, ChartValue } from 'features/charts-v2/types'
import type { ReferenceBounds } from 'features/charts-v2/types/chart-strategy'
import { LineChart } from 'features/charts/types'
import { getGroupedChartValues2 } from 'features/charts/utils'

export type AlertChartV2Result = {
  chart: ChartV2
  referenceBounds: ReferenceBounds | undefined
}

/**
 * Convert a v1 LineChart (charts[0] from the preview response) into a v2 Chart + optional
 * ReferenceBounds.
 *
 * @param currentChart  The LineChart from `previewChartResponse.charts[0]`
 * @param tier1         The alert scope (e.g. "product", "brand") — passed to
 *                      `getGroupedChartValues2` to resolve parent_values grouping
 */
export function buildAlertChartV2(
  currentChart: LineChart,
  tier1: string,
): AlertChartV2Result {
  // Long-format points — same source as v1 configureLineChart
  const values = getGroupedChartValues2(currentChart, [], tier1)

  // Separate bound series from the primary metric series.
  // Both groups use the 'Alert Bounds' substring as the discriminator (see file-level docstring).
  const ratingPoints = values.filter(
    (v) => !String(v.category ?? '').includes('Alert Bounds'),
  )
  const lowerPoints = values.filter(
    (v) =>
      String(v.category ?? '').includes('Alert Bounds') &&
      String(v.category ?? '').includes('Lower'),
  )
  const upperPoints = values.filter(
    (v) =>
      String(v.category ?? '').includes('Alert Bounds') &&
      String(v.category ?? '').includes('Upper'),
  )

  // Canonical bucket list: prefer x_values from the API (includes empty/gap buckets);
  // fall back to unique x_values from rating points.
  const rawBuckets: string[] = currentChart.x_values?.length
    ? currentChart.x_values.map((xv) => String(xv.x_value).replace("'", '20'))
    : [
        ...new Set(
          ratingPoints.map((v) => String(v.x_value ?? '').replace("'", '20')),
        ),
      ]

  // Sort chronologically so that chart data and bounds arrays align after the v2 transform's
  // own ascending sort.
  const sortedBuckets = [...rawBuckets].sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime(),
  )

  // Index rating / bound values by normalized x_value for O(1) per-bucket lookup.
  const ratingByX = new Map<string, number | null>()
  for (const v of ratingPoints) {
    const x = String(v.x_value ?? '').replace("'", '20')
    ratingByX.set(x, v.y_value != null ? Number(v.y_value) : null)
  }

  const lowerByX = new Map<string, number>()
  for (const v of lowerPoints) {
    const x = String(v.x_value ?? '').replace("'", '20')
    lowerByX.set(x, Number(v.y_value ?? 0))
  }

  const upperByX = new Map<string, number>()
  for (const v of upperPoints) {
    const x = String(v.x_value ?? '').replace("'", '20')
    upperByX.set(x, Number(v.y_value ?? 0))
  }

  // Build chart data (one ChartValue per bucket, null when the bucket has no rating data).
  const chartData: ChartValue[] = sortedBuckets.map((bucket) => ({
    category: [bucket],
    values: [ratingByX.get(bucket) ?? null],
  }))

  const chart: ChartV2 = {
    identifier: { aggregated_values: [], constraints: [], domains: [] },
    data: chartData,
  }

  // Build referenceBounds only when at least one bound series exists.
  const hasLower = lowerByX.size > 0
  const hasUpper = upperByX.size > 0

  const referenceBounds: ReferenceBounds | undefined =
    hasLower || hasUpper
      ? {
          ...(hasLower
            ? {
                lower: sortedBuckets.map(
                  (bucket) => lowerByX.get(bucket) ?? null,
                ),
              }
            : {}),
          ...(hasUpper
            ? {
                upper: sortedBuckets.map(
                  (bucket) => upperByX.get(bucket) ?? null,
                ),
              }
            : {}),
          label: 'Alert Bounds',
        }
      : undefined

  return { chart, referenceBounds }
}
